/**
 * Крупный просмотр картинки героини справа (по образцу `GenPreview`): цепочка итераций,
 * отзыв и следующая итерация, статусы, прошлые отзывы.
 *
 * Итерация платная: ключ идемпотентности живёт от ответа до ответа (новый — только после
 * успеха), оценка выше $1 требует второго клика, туда же ведёт 409 CONFIRM_REQUIRED —
 * ровно как в `LaunchPanel`. Пересборка из канона — это сам `LaunchPanel` в режиме `rebuild`.
 * Компонент монтируют с `key={imageId}`: другая картинка — чистая форма и новый ключ.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ExternalLink, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { LaunchPanel } from "@/components/persona/LaunchPanel";
import { defaultChosen, ModelChips } from "@/components/persona/ModelChips";
import { Btn, ErrorLine, Hint, inputClass } from "@/components/project/ui";
import { mediaUrl, when } from "@/lib/media";
import {
  estimateRun,
  estimateSourceText,
  estimateText,
  isConfirmRequired,
  iterateImage,
  loadImage,
  modelLabel,
  personaKeys,
  setImageStatus,
  usePersona,
  type ImageStatus,
  type ImageView,
  type ModelInfo,
} from "@/lib/persona";
import { errorText, newKey, usd } from "@/lib/project";

const CONFIRM_ABOVE = 1;
const STATUS_TEXT: Record<ImageStatus, string> = {
  new: "новая",
  rejected: "отклонена",
  approved: "одобрена",
  canon: "канон",
};

/** Глубина последнего одобренного: ищем среди цепочки и детей; нет — показываем id. */
function approvedLabel(data: ImageView): string {
  const hit = [...data.chain, ...data.children].find((c) => c.id === data.last_approved_id);
  return hit ? `v${hit.depth + 1}` : `#${data.last_approved_id}`;
}

export function ImageViewer({
  pid,
  imageId,
  models,
  onChange,
  onChanged,
}: {
  pid: number;
  imageId: number;
  models: ModelInfo[];
  onChange: (next: number | null) => void;
  onChanged: () => Promise<unknown>;
}) {
  const client = useQueryClient();
  const persona = usePersona(pid);
  const { data, error } = useQuery({ queryKey: personaKeys.image(imageId), queryFn: () => loadImage(imageId) });

  const [good, setGood] = useState("");
  const [bad, setBad] = useState("");
  const [chosenMap, setChosenMap] = useState(() => defaultChosen(models));
  const [fromApproved, setFromApproved] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rebuild, setRebuild] = useState(false);
  const key = useRef(newKey());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Esc в поле отзыва не должен закрывать панель и терять набранное.
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable || target?.closest?.("input, textarea, select, [contenteditable]")) return;
      onChange(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onChange]);

  // Итерируем той же моделью, которой сделана картинка: по умолчанию отмечена только она.
  const imageModel = data?.image.model;
  useEffect(() => {
    if (imageModel) setChosenMap(defaultChosen(models, imageModel));
  }, [imageModel, models]);

  const image = data?.image;
  const lastApproved = data?.last_approved_id ?? null;
  const canFromApproved = lastApproved !== null && image !== undefined && lastApproved !== image.id;
  const baseId = canFromApproved && fromApproved ? lastApproved : (image?.id ?? imageId);

  const chosen = models.filter((m) => chosenMap[m.code] && m.discovered).map((m) => m.code);
  const spec = Object.fromEntries(chosen.map((code) => [code, 1]));
  const chosenKey = chosen.join(",");

  // Другая база или набор моделей — другая цена: прежнее согласие больше не действует.
  useEffect(() => setConfirming(false), [chosenKey, baseId]);

  const estimate = useQuery({
    queryKey: ["persona-estimate", pid, "iterate", baseId, chosenKey],
    queryFn: () => estimateRun(pid, { models: spec, kind: "iterate", parent_image_id: baseId }),
    enabled: chosen.length >= 1 && image !== undefined,
  });
  const total = estimate.data?.total ?? null;

  const refresh = () =>
    Promise.all([client.invalidateQueries({ queryKey: personaKeys.image(imageId) }), onChanged()]);

  const iterate = useMutation({
    mutationFn: (confirm: boolean) =>
      iterateImage(baseId, {
        key: key.current,
        good: good.trim(),
        bad: bad.trim(),
        models: spec,
        ...(confirm ? { confirm: true } : {}),
      }),
    onSuccess: async () => {
      key.current = newKey();
      setConfirming(false);
      setGood("");
      setBad("");
      await refresh();
    },
    onError: (e) => {
      if (isConfirmRequired(e)) setConfirming(true);
    },
  });

  const status = useMutation({
    mutationFn: (next: "rejected" | "approved" | "canon") => setImageStatus(imageId, next),
    onSuccess: async () => {
      await refresh();
    },
  });

  const clickIterate = () => {
    if (iterate.isPending) return;
    if (!confirming && total !== null && total > CONFIRM_ABOVE) {
      setConfirming(true);
      return;
    }
    iterate.mutate(confirming);
  };

  // Версия промпта, которой сделана картинка, — из ленты запусков; иначе самая свежая.
  const view = persona.data;
  const run = view?.runs.find((r) => r.jobs.some((j) => j.images.some((i) => i.id === imageId)));
  const newest = view?.prompts.reduce<{ id: number; version: number } | null>(
    (best, p) => (best === null || p.version > best.version ? p : best),
    null,
  );
  const rebuildPromptId = view?.prompts.find((p) => p.version === run?.prompt_version)?.id ?? newest?.id ?? null;

  const goToRun = (runId: number) => document.getElementById(`run-${runId}`)?.scrollIntoView({ behavior: "smooth" });
  const showIterateError = iterate.isError && !isConfirmRequired(iterate.error);
  const sheet = image?.kind === "sheet";

  return (
    <aside
      data-annot="просмотр картинки героини"
      className="fixed top-[var(--spacing-topbar)] right-0 bottom-0 z-40 flex w-[min(440px,100vw)] flex-col gap-3 overflow-y-auto border-l border-border bg-bg-subtle p-3 pb-16 shadow-2xl"
    >
      <div className="flex items-baseline gap-2">
        <div className="min-w-0 flex-1 truncate text-[12.5px] text-fg-muted">
          {image ? `${sheet ? "лист" : "портрет"} · v${image.depth + 1} · ${STATUS_TEXT[image.status]}` : "картинка"}
        </div>
        <button
          type="button"
          title="закрыть (Esc)"
          onClick={() => onChange(null)}
          className="grid size-7 place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <X size={15} />
        </button>
      </div>

      {!data ? (
        error ? (
          <ErrorLine text={errorText(error)} />
        ) : (
          <Hint>Загружаю…</Hint>
        )
      ) : (
        <>
          <div className="flex min-h-[200px] flex-none items-center justify-center rounded-[var(--radius-ui)] bg-bg">
            <img
              src={mediaUrl(data.image.asset_path)}
              alt="картинка героини"
              className="block max-h-[52vh] max-w-full object-contain"
            />
          </div>

          <div className="tnum flex flex-wrap items-center gap-x-2 text-[11.5px] text-fg-faint">
            <span>
              {[
                modelLabel(models, data.job.model),
                data.image.cost !== null ? usd(data.image.cost) : "",
                when(data.image.date_create),
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
            <a
              href={mediaUrl(data.image.asset_path)}
              target="_blank"
              rel="noopener"
              className="ml-auto inline-flex items-center gap-1 text-[12.5px] text-accent-text hover:underline"
            >
              оригинал <ExternalLink size={12} />
            </a>
          </div>

          {data.chain.length > 1 || data.children.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1 text-[12px] text-fg-muted">
              {data.chain.map((c, i) => (
                <span key={c.id} className="inline-flex items-center gap-1">
                  {i > 0 ? <span className="text-fg-faint">→</span> : null}
                  <button
                    type="button"
                    onClick={() => onChange(c.id)}
                    className={clsx(
                      "rounded-[var(--radius-ui)] px-1.5 py-0.5",
                      c.id === data.image.id
                        ? "bg-accent-dim font-medium text-accent-text"
                        : "hover:bg-bg-hover hover:text-fg",
                    )}
                  >
                    v{c.depth + 1}
                  </button>
                </span>
              ))}
              {data.children.length > 0 ? (
                <span className="ml-2 inline-flex flex-wrap items-center gap-1 text-fg-faint">
                  дочерние:
                  {data.children.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onChange(c.id)}
                      className="rounded-[var(--radius-ui)] px-1.5 py-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
                    >
                      v{c.depth + 1}
                    </button>
                  ))}
                </span>
              ) : null}
            </div>
          ) : null}

          {data.drift_warning ? (
            <div className="grid gap-2 rounded-[var(--radius-ui)] bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
              <div>глубина {data.image.depth} — возможен дрейф лица</div>
              <div>
                <Btn onClick={() => setRebuild((v) => !v)}>Пересобрать из канона</Btn>
              </div>
            </div>
          ) : null}
          {rebuild ? (
            <LaunchPanel
              pid={pid}
              mode="rebuild"
              promptId={rebuildPromptId}
              models={models}
              onLaunched={() => {
                setRebuild(false);
                void onChanged();
              }}
              onCancelMode={() => setRebuild(false)}
            />
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Btn
              tone="danger"
              disabled={status.isPending}
              className={data.image.status === "rejected" ? "bg-err-bg" : undefined}
              onClick={() => status.mutate("rejected")}
            >
              Отклонить
            </Btn>
            <Btn
              disabled={status.isPending}
              className={data.image.status === "approved" ? "border-accent bg-accent-dim text-accent-text" : undefined}
              onClick={() => status.mutate("approved")}
            >
              Одобрить
            </Btn>
            <Btn
              disabled={status.isPending}
              className={data.image.status === "canon" ? "border-accent bg-accent-dim text-accent-text" : undefined}
              onClick={() => {
                if (data.image.status !== "canon") status.mutate("canon");
              }}
            >
              {sheet ? "Сделать каноном листа" : "Сделать каноном"}
            </Btn>
          </div>
          <ErrorLine text={status.isError ? errorText(status.error) : null} />

          <div className="grid gap-2 rounded-[var(--radius-card)] border border-border bg-bg p-3">
            <label className="grid gap-1 text-[11.5px] text-fg-faint">
              Что хорошо (оставить)
              <textarea
                className={clsx(inputClass, "h-auto min-h-16 py-1.5")}
                value={good}
                onChange={(e) => setGood(e.target.value)}
              />
            </label>
            <label className="grid gap-1 text-[11.5px] text-fg-faint">
              Что плохо (изменить)
              <textarea
                className={clsx(inputClass, "h-auto min-h-16 py-1.5")}
                value={bad}
                onChange={(e) => setBad(e.target.value)}
              />
            </label>
            <ModelChips models={models} value={chosenMap} onChange={setChosenMap} />
            {canFromApproved ? (
              <label className="flex items-center gap-2 text-[12.5px] text-fg-muted">
                <input type="checkbox" checked={fromApproved} onChange={(e) => setFromApproved(e.target.checked)} />
                От последнего одобренного
                {fromApproved ? <span className="text-fg-faint">· база: {approvedLabel(data)}</span> : null}
              </label>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <Btn
                tone="primary"
                onClick={clickIterate}
                disabled={iterate.isPending || chosen.length === 0}
                className={confirming ? "bg-warn text-bg" : undefined}
              >
                {iterate.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
                {confirming ? `Да, запустить за ${usd(total, 2)}` : "Следующая итерация"}
              </Btn>
              <span className="tnum text-[12.5px] text-fg-muted">
                {chosen.length === 0
                  ? "выберите модель"
                  : total === null
                    ? estimate.isError
                      ? "оценка недоступна"
                      : "считаю…"
                    : `итого ${estimateText(total, estimate.data?.source === "fact" ? "fact" : "spike")}${estimateSourceText(
                        estimate.data?.source,
                      )}`}
              </span>
            </div>
            {confirming ? <Hint>Дороже ${CONFIRM_ABOVE}: нажмите ещё раз, чтобы подтвердить.</Hint> : null}
            {iterate.isSuccess ? (
              <Hint>
                {iterate.data.reused
                  ? "такой же запрос уже в работе"
                  : `итерация запущена, промпт v${iterate.data.prompt.version}, стоило ${usd(iterate.data.estimate)}`}
              </Hint>
            ) : null}
            <ErrorLine text={showIterateError ? errorText(iterate.error) : null} />
            <ErrorLine text={estimate.isError ? errorText(estimate.error) : null} />
          </div>

          {data.feedbacks.length > 0 ? (
            <div className="grid gap-2">
              <Hint>Прошлые отзывы</Hint>
              {data.feedbacks.map((f) => (
                <div key={f.id} className="grid gap-0.5 rounded-[var(--radius-ui)] bg-bg p-2 text-[12px]">
                  {f.good ? <div className="text-ok">+ {f.good}</div> : null}
                  {f.bad ? <div className="text-err">− {f.bad}</div> : null}
                  <div className="text-[11.5px] text-fg-faint">
                    {f.author_name} · {when(f.date_create)}
                    {f.run_id !== null ? (
                      <>
                        {" · "}
                        <button
                          type="button"
                          className="text-accent-text hover:underline"
                          onClick={() => goToRun(f.run_id as number)}
                        >
                          запуск #{f.run_id}
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </aside>
  );
}
